import { useState } from 'react';
import { durationOptions, regions, timeOptions } from '@/mocks/categories';

export interface FilterState {
  region: string;
  duration: string;
  time: string;
}

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  activeCount: number;
}

function ChipGroup({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: { id: string; name: string }[];
  value: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <span className="w-20 shrink-0 text-xs font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onSelect('all')}
          className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
            value === 'all'
              ? 'bg-primary-500 font-medium text-foreground-950'
              : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
          }`}
        >
          全部
        </button>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onSelect(o.id)}
            className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
              value === o.id
                ? 'bg-primary-500 font-medium text-foreground-950'
                : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
            }`}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ value, onChange, onReset, activeCount }: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const regionOpts = regions.map((r) => ({ id: r, name: r }));
  const durationOpts = durationOptions.map((d) => ({ id: d.id, name: d.name }));
  const timeOpts = timeOptions.map((t) => ({ id: t.id, name: t.name }));

  const content = (
    <div className="flex flex-col gap-4">
      <ChipGroup
        label="地区"
        options={regionOpts}
        value={value.region}
        onSelect={(id) => onChange({ ...value, region: id })}
      />
      <ChipGroup
        label="时长"
        options={durationOpts}
        value={value.duration}
        onSelect={(id) => onChange({ ...value, duration: id })}
      />
      <ChipGroup
        label="发布时间"
        options={timeOpts}
        value={value.time}
        onSelect={(id) => onChange({ ...value, time: id })}
      />
      <div className="flex items-center justify-between border-t border-background-200 pt-3">
        <span className="text-xs text-foreground-500">
          已选择 {activeCount} 个筛选条件
        </span>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-200 hover:text-primary-300"
        >
          <i className="ri-refresh-line" />
          重置筛选
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop inline */}
      <div className="hidden rounded-lg border border-background-200/70 bg-background-100 p-4 md:block">
        {content}
      </div>

      {/* Mobile trigger */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-background-200/70 bg-background-100 py-3 text-sm font-medium text-foreground-800 md:hidden"
      >
        <i className="ri-filter-3-line" />
        筛选条件
        {activeCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs text-foreground-950">
            {activeCount}
          </span>
        )}
      </button>

      {/* Mobile sheet */}
      {sheetOpen && (
        <div className="fixed inset-0 z-[70] flex items-end bg-black/70 md:hidden" onClick={() => setSheetOpen(false)}>
          <div
            className="w-full animate-fade-up rounded-t-2xl border-t border-background-200 bg-background-100 p-5 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-foreground-700"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            {content}
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-5 w-full rounded-full bg-primary-500 py-3 text-sm font-semibold text-foreground-950"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}